import { NumberInput, Select } from '@mantine/core';
import type { FlexibleDate, DatePrecision } from '../../types/medicalHistory';

interface FlexibleDateInputProps {
  label?: string;
  value: FlexibleDate | null;
  onChange: (date: FlexibleDate | null) => void;
  disabled?: boolean;
}

const MONTH_OPTIONS = [
  { value: '01', label: 'Jan' },
  { value: '02', label: 'Feb' },
  { value: '03', label: 'Mar' },
  { value: '04', label: 'Apr' },
  { value: '05', label: 'May' },
  { value: '06', label: 'Jun' },
  { value: '07', label: 'Jul' },
  { value: '08', label: 'Aug' },
  { value: '09', label: 'Sep' },
  { value: '10', label: 'Oct' },
  { value: '11', label: 'Nov' },
  { value: '12', label: 'Dec' },
];

const currentYear = new Date().getFullYear();

// Parse existing date value into parts
function parseDateValue(value: FlexibleDate | null): {
  year: number | '';
  month: string;
  day: number | '';
} {
  if (!value) {
    return { year: '', month: '', day: '' };
  }

  const parts = value.value.split('-');
  return {
    year: parts[0] ? parseInt(parts[0], 10) : '',
    month: parts[1] || '',
    day: parts[2] ? parseInt(parts[2], 10) : '',
  };
}

// Build FlexibleDate from parts, determining precision automatically
function buildFlexibleDate(
  year: number | '',
  month: string,
  day: number | ''
): FlexibleDate | null {
  if (!year) return null;

  let value: string;
  let precision: DatePrecision;

  if (month && day) {
    value = `${year}-${month}-${String(day).padStart(2, '0')}`;
    precision = 'day';
  } else if (month) {
    value = `${year}-${month}`;
    precision = 'month';
  } else {
    value = String(year);
    precision = 'year';
  }

  return { value, precision };
}

export function FlexibleDateInput({
  label,
  value,
  onChange,
  disabled = false,
}: FlexibleDateInputProps) {
  const { year, month, day } = parseDateValue(value);

  const handleYearChange = (newYear: number | string) => {
    const y = typeof newYear === 'number' ? newYear : '';
    onChange(buildFlexibleDate(y, month, day));
  };

  const handleMonthChange = (newMonth: string | null) => {
    const m = newMonth || '';
    // Clear day if month is cleared
    const d = m ? day : '';
    onChange(buildFlexibleDate(year, m, d));
  };

  const handleDayChange = (newDay: number | string) => {
    const d = typeof newDay === 'number' ? newDay : '';
    onChange(buildFlexibleDate(year, month, d));
  };

  // Calculate max days for selected month/year
  const getMaxDays = () => {
    if (!year || !month) return 31;
    const m = parseInt(month, 10);
    const y = typeof year === 'number' ? year : parseInt(String(year), 10);
    return new Date(y, m, 0).getDate();
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="text-sm font-medium text-gray-300">{label}</label>
      )}

      <div className="flex gap-2">
        {/* Year - always shown, primary field */}
        <div className="w-24">
          <NumberInput
            placeholder="Year"
            value={year}
            onChange={handleYearChange}
            disabled={disabled}
            min={1900}
            max={currentYear}
            hideControls
            classNames={{
              input: 'input text-center',
            }}
          />
        </div>

        {/* Month - optional */}
        <div className="w-24">
          <Select
            placeholder="Month"
            value={month || null}
            onChange={handleMonthChange}
            disabled={disabled || !year}
            data={MONTH_OPTIONS}
            classNames={{
              input: `input ${!year ? 'opacity-50' : ''}`,
              dropdown: 'bg-gray-900 border-gray-700',
              option: 'hover:bg-gray-800 text-gray-200',
            }}
            clearable
          />
        </div>

        {/* Day - optional, requires month */}
        <div className="w-20">
          <NumberInput
            placeholder="Day"
            value={day}
            onChange={handleDayChange}
            disabled={disabled || !month}
            min={1}
            max={getMaxDays()}
            hideControls
            classNames={{
              input: `input text-center ${!month ? 'opacity-50' : ''}`,
            }}
          />
        </div>
      </div>

      <p className="text-xs text-gray-500">Estimate is OK - year only is fine</p>
    </div>
  );
}
